import { useEffect, useState } from "preact/hooks";
import type { Character, ManualIWR } from "../character";
import { IWR_KINDS, IWR_TABLE_BY_KIND, cleanManualIWR } from "../character";
import { loadConfig } from "../data/config";
import type { DerivedSheet, IWRView } from "../engine";
import { Modal } from "./modal";

interface Props {
  character: Character;
  sheet: DerivedSheet;
  busy: boolean;
  onSave: (iwr: ManualIWR[]) => Promise<void>;
  onClose: () => void;
}

type Kind = ManualIWR["kind"];
type Tables = Record<Kind, Record<string, string>>;

const KIND_LABELS: Record<Kind, string> = { immunity: "Immunities", weakness: "Weaknesses", resistance: "Resistances" };
const LIST_OF: Record<Kind, keyof DerivedSheet["iwr"]> = { immunity: "immunities", weakness: "weaknesses", resistance: "resistances" };
/** How many exceptions or doubled types one entry takes (Foundry's editor caps its tags at six). */
const MAX_TAGS = 6;

/**
 * Immunities, weaknesses and resistances entered by hand (#22), as Foundry's IWR editor takes
 * them: a type from the Snapshot's table for the kind, a value of 1 or more (none on an
 * immunity), exceptions, doubled types on a resistance, and a note like an Override's. The
 * derived entries are listed beside them, read-only, with what granted them.
 */
export function IWREditor({ character, sheet, busy, onSave, onClose }: Props) {
  const [rows, setRows] = useState<ManualIWR[]>(() => character.iwr.map((e) => ({ ...e, exceptions: [...(e.exceptions ?? [])], doubleVs: [...(e.doubleVs ?? [])] })));
  const [tables, setTables] = useState<Tables | null>(null);
  useEffect(() => {
    let live = true;
    loadConfig()
      .then((cfg) => {
        if (!live) return;
        const table = (kind: Kind): Record<string, string> => Object.fromEntries(Object.entries(cfg.tables[IWR_TABLE_BY_KIND[kind]] ?? {}).filter(([k]) => k !== "custom"));
        setTables({ immunity: table("immunity"), weakness: table("weakness"), resistance: table("resistance") });
      })
      .catch(() => live && setTables({ immunity: {}, weakness: {}, resistance: {} }));
    return () => { live = false; };
  }, []);
  const update = (index: number, change: Partial<ManualIWR>) => setRows(rows.map((r, i) => (i === index ? { ...r, ...change } : r)));
  const remove = (index: number) => setRows(rows.filter((_, i) => i !== index));
  const add = (kind: Kind) => setRows([...rows, kind === "immunity" ? { kind, type: "" } : { kind, type: "", value: 1 }]);
  const save = async () => {
    const cleaned = rows.map(cleanManualIWR).filter((e): e is ManualIWR => e !== null);
    await onSave(cleaned);
  };
  return (
    <Modal title="Immunities, weaknesses, resistances" onClose={onClose} class="iwr-editor">
      <p class="muted">Entries added here sit beside the ones the rules derive. When both name a type, the higher value wins, as Foundry merges them.</p>
      {IWR_KINDS.map((kind) => {
        const derived = sheet.iwr[LIST_OF[kind]].filter((v) => !v.manual);
        const own = rows.map((r, index) => ({ r, index })).filter(({ r }) => r.kind === kind);
        return (
          <section key={kind} class="iwr-section" aria-label={KIND_LABELS[kind]}>
            <h3>{KIND_LABELS[kind]}</h3>
            {derived.length > 0 && (
              <ul class="plain iwr-derived">
                {derived.map((v, i) => <li key={i}><span>{v.label}</span> <span class="muted">· {v.sourceName ?? "derived"}</span></li>)}
              </ul>
            )}
            {own.map(({ r, index }) => <Row key={index} entry={r} types={tables ? tables[kind] : null} busy={busy} onChange={(change) => update(index, change)} onRemove={() => remove(index)} />)}
            <button type="button" disabled={busy} onClick={() => add(kind)}>Add {kind}</button>
          </section>
        );
      })}
      <div class="actions roll-actions">
        <button type="button" class="primary" disabled={busy || rows.some((r) => !r.type)} onClick={() => void save()}>Save</button>
        <button type="button" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

function Row({ entry, types, busy, onChange, onRemove }: { entry: ManualIWR; types: Record<string, string> | null; busy: boolean; onChange: (change: Partial<ManualIWR>) => void; onRemove: () => void }) {
  const known = types ?? {};
  const options = Object.entries(known).sort((a, b) => a[1].localeCompare(b[1]));
  const label = (type: string) => known[type] ?? type.replace(/-/g, " ");
  const tagList = (field: "exceptions" | "doubleVs", title: string) => {
    const current = entry[field] ?? [];
    const candidates = options.filter(([k]) => k !== entry.type && !current.includes(k));
    return (
      <span class="iwr-tags">
        <span class="stat-label">{title}</span>
        {current.map((t) => (
          <span key={t} class="tag">{label(t)} <button type="button" class="linkish" aria-label={`Remove ${title.toLowerCase()} ${label(t)}`} disabled={busy} onClick={() => onChange({ [field]: current.filter((c) => c !== t) })}>×</button></span>
        ))}
        {current.length < MAX_TAGS && (
          <select aria-label={`Add ${title.toLowerCase()}`} value="" disabled={busy} onChange={(e) => { const v = (e.currentTarget as HTMLSelectElement).value; if (v) onChange({ [field]: [...current, v] }); }}>
            <option value="">add…</option>
            {candidates.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        )}
      </span>
    );
  };
  return (
    <div class="iwr-row card">
      <div class="inline">
        <select aria-label={`${entry.kind} type`} value={entry.type} disabled={busy} onChange={(e) => onChange({ type: (e.currentTarget as HTMLSelectElement).value })}>
          <option value="">type…</option>
          {entry.type && !known[entry.type] && <option value={entry.type}>{label(entry.type)}</option>}
          {options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {entry.kind !== "immunity" && (
          <label class="override-value"><span class="stat-label">Value</span><input type="number" min="1" inputMode="numeric" aria-label={`${entry.kind} value`} value={entry.value ?? 1} disabled={busy} onInput={(e) => onChange({ value: Math.max(1, Math.trunc(Number((e.currentTarget as HTMLInputElement).value)) || 1) })} /></label>
        )}
        <button type="button" disabled={busy} onClick={onRemove}>Remove</button>
      </div>
      <div class="inline">
        {tagList("exceptions", "Except")}
        {entry.kind === "resistance" && tagList("doubleVs", "Double vs.")}
      </div>
      <input type="text" aria-label="Note" placeholder="Note (optional): where it comes from" value={entry.note ?? ""} disabled={busy} onInput={(e) => onChange({ note: (e.currentTarget as HTMLInputElement).value })} />
    </div>
  );
}

/** "fire 2 · by hand" or "fire 5 · Arctic Elf": how the Overview lists an entry (#22). */
export function iwrSourceLabel(view: IWRView): string {
  if (view.manual && (view.sourceName === null || view.manual.value === view.value)) return view.manual.note ? `by hand · ${view.manual.note}` : "by hand";
  if (view.manual) return `${view.sourceName ?? "derived"} · by hand ${view.manual.value ?? ""}${view.manual.note ? ` (${view.manual.note})` : ""}`.trim();
  return view.sourceName ?? "";
}
